import React, { Component, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import './styles/progressTableRefresh.css';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { GradeTrackLeaveProvider } from './contexts/GradeTrackLeaveContext';
import { ProgramSidebarProvider } from './contexts/ProgramSidebarContext';
import MainLayout from './layouts/MainLayout';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import LandingIntroPage from './pages/LandingIntroPage';
import LandingPassPurchasePage from './pages/LandingPassPurchasePage';
import LandingQaPage from './pages/LandingQaPage';
import LandingNoticesPage from './pages/LandingNoticesPage';
import LoginModal from './components/auth/LoginModal';
import SignUpModal from './components/auth/SignUpModal';
import AcademicCalendarPage from './pages/AcademicCalendarPage';
import ClassSchedulePage from './pages/ClassSchedulePage';
import AnnualTimetablePage from './pages/AnnualTimetablePage';
import ClassRecordMemosPage from './pages/ClassRecordMemosPage';
import WeeklyLearningGuidePage from './pages/WeeklyLearningGuidePage';
import GradeTrackPage from './pages/GradeTrackPage';
import AnnualProgressPage from './pages/AnnualProgressPage';
import ProgramFixedPage from './pages/ProgramFixedPage';
import ProgramProjectPage from './pages/ProgramProjectPage';
import ProgramTrackPage from './pages/ProgramTrackPage';
import AdminCrossCurricularPage from './pages/AdminCrossCurricularPage';
import AdminLearningPlanActivityPage from './pages/AdminLearningPlanActivityPage';
import AdminEvaluationPlanPage from './pages/AdminEvaluationPlanPage';
import AdminLearningContentPage from './pages/AdminLearningContentPage';
import WorkspaceLearningContentPage from './pages/WorkspaceLearningContentPage';
import WorkspaceEvaluationPlanPage from './pages/WorkspaceEvaluationPlanPage';
import AdminUsersAndPassPage from './pages/AdminUsersAndPassPage';
import AdminPaymentOptionsPage from './pages/AdminPaymentOptionsPage';
import AdminPlanPermissionsPage from './pages/AdminPlanPermissionsPage';
import AdminBetaTesterPage from './pages/AdminBetaTesterPage';
import AdminLegalDocumentsPage from './pages/AdminLegalDocumentsPage';
import AdminNoticesPage from './pages/AdminNoticesPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import AdminProfileCardSetsPage from './pages/AdminProfileCardSetsPage';
import AdminProfileBubblesPage from './pages/AdminProfileBubblesPage';
import AdminLandingPageCMS from './pages/AdminLandingPageCMS';
import BetaTesterFeedbackPage from './pages/BetaTesterFeedbackPage';
import BudgetNotePage from './pages/BudgetNotePage';
import DailyNotePage from './pages/DailyNotePage';
import AssignmentPointsNotePage from './pages/AssignmentPointsNotePage';
import PointItemNotePage from './pages/PointItemNotePage';
import ProgramContentSlidePage from './pages/ProgramContentSlidePage';
import WeeklyLearningGuideA4PreviewPage from './pages/WeeklyLearningGuideA4PreviewPage';
import DocumentExportPreviewPage from './pages/DocumentExportPreviewPage';
import PublicPresentationPage from './pages/PublicPresentationPage';
import SharedTodoNotePublicPage from './pages/SharedTodoNotePublicPage';
import ChallengePublicPage from './pages/ChallengePublicPage';
import DashboardPresentationPage from './pages/DashboardPresentationPage';
import RemoteControllerPage from './pages/RemoteControllerPage';
import CommStudentPage from './pages/CommStudentPage';
import DashboardPage from './pages/DashboardPage';
import TodoNotePage from './pages/TodoNotePage';
import SubscriptionPassPage from './pages/SubscriptionPassPage';
import {
  getCurrentUserDisplay,
  getPendingAccountDeletion,
  getSession,
  onAuthStateChange,
  signOut,
  UserDisplayInfo,
  type PendingAccountDeletion,
} from './services/authService';
import { startUserLastActiveDailyWatch } from './utils/userLastActiveDaily';
import { markTodoNoteLoginHeadSyncPending } from './utils/todoNoteLoginHeadSync';
import { markBudgetNoteLoginHeadSyncPending } from './utils/budgetNoteLoginHeadSync';
import { markSchoolDataLoginHeadSyncPending } from './utils/schoolDataLoginHeadSync';
import BrandLogo from './components/BrandLogo';
import CachedAppImage from './components/common/CachedAppImage';
import { hasCurrentLegalConsent, recordLegalConsent } from './services/legalConsentService';
import LegalConsentModal from './components/auth/LegalConsentModal';
import { removeAllNoteQueries } from './query/noteCacheUpdates';
import { clearAdminLearningContentSessionDbSync } from './utils/adminLearningContentSessionSync';
import { clearAdminLearningContentMemoryCaches } from './utils/adminLearningContentCache';
import { clearAdminEvaluationPlanSessionDbSync } from './utils/adminEvaluationPlanSessionSync';
import {
  consumeAuthOAuthPending,
  ensureAuthActivityBaseline,
  isAuthIdleExpired,
  startAuthIdleWatch,
  touchAuthActivity,
} from './utils/authIdleTimeout';
import { clearProgramScopedScopeServerValidatedRegistry } from './utils/programScopedRowsServerValidatedSession';
import { getGrades } from './services/gradeTrackService';
import { ensureSchoolContext } from './services/schoolContextService';
import { Grade, Workspace } from './types/domain';
import { resetSchoolDataCacheSyncState } from './utils/schoolDataCacheVersion';
import { syncSchoolCachesWithQuery } from './query/syncSchoolCachesWithQuery';
import { SchoolQueryInvalidationHost } from './query/hooks/useSchoolQueryInvalidation';
import {
  clearPostLoginSchoolDataNetworkRefresh,
  markPostLoginSchoolDataNetworkRefresh,
} from './utils/postLoginSchoolDataRefresh';
import { clearSchoolNoticeAcks } from './utils/schoolNoticeSeenStorage';
import { PLATFORM_CONTENT_SCOPE_KEY } from './constants/platformContentScope';
import { installDevNetworkTransferMonitor } from './utils/devNetworkTransferMonitor';
import {
  getUserWorkspaceUiPreferences,
  resolveGradeIdInList,
  resolveNavGradeIdFromPreferences,
  saveUserWorkspaceUiPreferences,
} from './services/userWorkspaceUiPreferencesService';
import { APP_VERSION } from './appMeta';
import { applyAppDocumentTitle } from './utils/appDocumentTitle';
import LoginSchoolNoticeGate from './components/schoolNotice/LoginSchoolNoticeGate';
import {
  markPostLoginPassRedirect,
  peekPendingPassCheckout,
  takePostLoginRedirect,
} from './utils/pendingPassCheckout';
import { bindSubscriptionPassUserId } from './utils/subscriptionPassStorage';
import RecoveryPasswordFormModal from './components/auth/RecoveryPasswordFormModal';
import PendingAccountDeletionScreen from './components/auth/PendingAccountDeletionScreen';
import LegalDocumentPage, { getLegalDocKindFromPath } from './pages/LegalDocumentPage';

const GRADE_KEY = 'curriculumplanner-grade-id';

/** 세션/워크스페이스 부트스트랩이 영구 대기할 때 스플래시에서 벗어나게 함 */
const BOOTSTRAP_SESSION_MS = 20_000;
const BOOTSTRAP_WORKSPACE_MS = 90_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => {
      const portHint =
        typeof window !== 'undefined' &&
        window.location.port &&
        window.location.port !== '80' &&
        window.location.port !== '443'
          ? ` (현재 포트 ${window.location.port}: Supabase Authentication → Redirect URLs에 http://localhost:${window.location.port}/ 를 등록했는지 확인하세요.)`
          : '';
      reject(new Error(`${label}: ${Math.round(ms / 1000)}초 안에 응답이 없습니다.${portHint}`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) window.clearTimeout(timer);
  });
}

function isPublicPresentationPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return /^\/p\/[^/?#]+/.test(path);
}

function isSharedTodoNotePublicPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return /^\/t\/[^/?#]+/.test(path);
}

function isChallengePublicPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return /^\/c\/[^/?#]+/.test(path);
}

function appWindowPlaceholderStatusLabel(): string {
  try {
    const status = new URLSearchParams(window.location.search).get('status');
    if (status === 'ai-generating') return 'AI 생성 중';
  } catch {
    /* ignore */
  }
  return '';
}

function isAppWindowPlaceholderPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/app-window' || path.startsWith('/app-window/');
}

function isProgramContentSlidePath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/program-content-slide' || path.startsWith('/program-content-slide/');
}

/** /comm/:roomId (학생, 로그인 없음) · /comm-host (교사) — 소통 프레젠테이션 */
function commPathKind(): 'student' | 'host' | null {
  if (typeof window === 'undefined') return null;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) path = path.slice(base.length) || '/';
  if (path === '/comm-host') return 'host';
  if (/^\/comm\/[^/]+\/?$/.test(path)) return 'student';
  return null;
}

function isRemoteControllerPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/remote' || path.startsWith('/remote/');
}

function isDashboardPresentationPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/dashboard-presentation' || path.startsWith('/dashboard-presentation/');
}

function isWeeklyLearningGuideA4PreviewPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/weekly-learning-guide-a4-preview' || path.startsWith('/weekly-learning-guide-a4-preview/');
}

function isDocumentExportPreviewPath(): boolean {
  if (typeof window === 'undefined') return false;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) {
    path = path.slice(base.length) || '/';
  }
  return path === '/document-export-preview' || path.startsWith('/document-export-preview/');
}

function isStandaloneExportPreviewPath(): boolean {
  return isWeeklyLearningGuideA4PreviewPath() || isDocumentExportPreviewPath();
}

/** 랜딩 이용권 결제 등: 로그인 직후 보류된 경로로 이동 */
function PostLoginRedirectHost() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const redirected = takePostLoginRedirect();
    if (redirected && redirected !== location.pathname) {
      navigate(redirected, { replace: true });
      return;
    }
    if (location.pathname === '/pass') {
      navigate('/subscription', { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

/** 구 사이드바 경로 → 대시보드 + 클래스 멤버 설정 모달 */
function ClassMembersDeepLink({ onOpen }: { onOpen: () => void }) {
  useEffect(() => {
    onOpen();
  }, [onOpen]);
  return <Navigate to="/dashboard" replace />;
}

/** 렌더 중 발생한 오류를 잡아 빈 화면 대신 안내를 보여줌 */
class AppErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown) {
    console.error('AppErrorBoundary:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bootstrap-state" role="alert">
          <h1 className="bootstrap-splash-title">
            <BrandLogo className="cp-brand-logo--splash" />
          </h1>
          <p className="bootstrap-state-version" aria-label={`앱 버전 ${APP_VERSION}`}>
            v{APP_VERSION}
          </p>
          <p className="error-line">
            {/failed to fetch|network/i.test(this.state.message)
              ? '네트워크 연결을 확인해 주세요.'
              : this.state.message || '오류가 발생했습니다.'}
          </p>
          <p className="bootstrap-state-hint">
            새로고침 후 다시 시도해 주세요.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

interface AnnualProgressKeepAliveRoutesProps {
  workspaceId: string;
  grades: Grade[];
  currentGradeId: string;
}

interface StaticPageKeepAliveRoutesProps {
  workspaceId: string;
  grades: Grade[];
  currentGradeId: string;
  onSwitchToClassIntegrated: () => void;
  onNavGradeChange: (gradeId: string) => void;
}

function ProgramKeepAliveRoutes() {
  const location = useLocation();
  const [visited, setVisited] = useState({ fixed: false, project: false, track: false });
  const isFixed = location.pathname === '/program/fixed';
  const isProject = location.pathname === '/program/project' || location.pathname.startsWith('/program/project/');
  const isTrack = location.pathname.startsWith('/program/track/');

  useEffect(() => {
    setVisited((prev) => {
      let next = prev;
      if (isFixed && !prev.fixed) next = { ...next, fixed: true };
      if (isProject && !prev.project) next = { ...next, project: true };
      if (isTrack && !prev.track) next = { ...next, track: true };
      return next === prev ? prev : next;
    });
  }, [isFixed, isProject, isTrack]);

  const keepAliveStyle = (active: boolean): React.CSSProperties => ({
    display: active ? 'flex' : 'none',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  });

  return (
    <>
      {visited.fixed ? (
        <div className="cp-keepalive-route" style={keepAliveStyle(isFixed)} aria-hidden={!isFixed}>
          <ProgramFixedPage isActive={isFixed} />
        </div>
      ) : null}
      {visited.project ? (
        <div className="cp-keepalive-route" style={keepAliveStyle(isProject)} aria-hidden={!isProject}>
          <ProgramProjectPage isActive={isProject} />
        </div>
      ) : null}
      {visited.track ? (
        <div className="cp-keepalive-route" style={keepAliveStyle(isTrack)} aria-hidden={!isTrack}>
          <ProgramTrackPage isActive={isTrack} />
        </div>
      ) : null}
    </>
  );
}

function AnnualProgressKeepAliveRoutes({
  workspaceId,
  grades,
  currentGradeId,
}: AnnualProgressKeepAliveRoutesProps) {
  const location = useLocation();
  const [visited, setVisited] = useState<{ 1: boolean; 2: boolean }>({ 1: false, 2: false });
  const activeTerm = location.pathname === '/annual-progress-term1' ? 1 : location.pathname === '/annual-progress-term2' ? 2 : null;

  useEffect(() => {
    if (!activeTerm) return;
    setVisited((prev) => {
      if (prev[activeTerm]) return prev;
      return { ...prev, [activeTerm]: true };
    });
  }, [activeTerm]);

  const keepAliveStyle = (active: boolean): React.CSSProperties => ({
    display: active ? 'block' : 'none',
    minHeight: 0,
  });

  return (
    <>
      {visited[1] ? (
        <div
          className="cp-keepalive-route"
          style={keepAliveStyle(activeTerm === 1)}
          aria-hidden={activeTerm !== 1}
        >
          <AnnualProgressPage
            workspaceId={workspaceId}
            grades={grades}
            currentGradeId={currentGradeId}
            termNumber={1}
            isActive={activeTerm === 1}
          />
        </div>
      ) : null}
      {visited[2] ? (
        <div
          className="cp-keepalive-route"
          style={keepAliveStyle(activeTerm === 2)}
          aria-hidden={activeTerm !== 2}
        >
          <AnnualProgressPage
            workspaceId={workspaceId}
            grades={grades}
            currentGradeId={currentGradeId}
            termNumber={2}
            isActive={activeTerm === 2}
          />
        </div>
      ) : null}
    </>
  );
}

const STATIC_KEEP_ALIVE_PATHS = [
  '/dashboard',
  '/grade-track',
  '/academic-calendar',
  '/class-schedule',
  '/workspace-learning-content',
  '/workspace-evaluation-plan',
  '/annual-timetable',
  '/class-record-memos',
  '/weekly-learning-guide',
  '/todo-note',
  '/todo-note/account',
] as const;

function StaticPageKeepAliveRoutes({
  workspaceId,
  grades,
  currentGradeId,
  onSwitchToClassIntegrated,
  onNavGradeChange,
}: StaticPageKeepAliveRoutesProps) {
  const location = useLocation();
  const activePath = STATIC_KEEP_ALIVE_PATHS.find((path) => path === location.pathname) ?? null;
  const [visited, setVisited] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activePath) return;
    setVisited((prev) => {
      if (prev[activePath]) return prev;
      return { ...prev, [activePath]: true };
    });
  }, [activePath]);

  const renderPage = (path: (typeof STATIC_KEEP_ALIVE_PATHS)[number], node: React.ReactNode) => {
    if (!visited[path] && activePath !== path) return null;
    const isActive = activePath === path;
    return (
      <div
        key={path}
        className="cp-keepalive-route"
        style={{
          display: isActive ? 'flex' : 'none',
          flexDirection: 'column',
          minHeight: 0,
        }}
        aria-hidden={!isActive}
      >
        {node}
      </div>
    );
  };

  return (
    <>
      {renderPage(
        '/dashboard',
        <DashboardPage
          workspaceId={workspaceId}
          currentGradeId={currentGradeId}
          grades={grades}
          onSwitchToClassIntegrated={onSwitchToClassIntegrated}
        />
      )}
      {renderPage(
        '/grade-track',
        <GradeTrackPage
          workspaceId={workspaceId}
          currentGradeId={currentGradeId}
          grades={grades}
          onSwitchToClassIntegrated={onSwitchToClassIntegrated}
        />
      )}
      {renderPage(
        '/academic-calendar',
        <AcademicCalendarPage
          workspaceId={workspaceId}
          currentGradeId={currentGradeId}
          grades={grades}
          onNavGradeChange={onNavGradeChange}
        />
      )}
      {renderPage(
        '/class-schedule',
        <ClassSchedulePage
          workspaceId={workspaceId}
          currentGradeId={currentGradeId}
          grades={grades}
          onNavGradeChange={onNavGradeChange}
        />
      )}
      {renderPage(
        '/workspace-learning-content',
        <WorkspaceLearningContentPage
          currentGradeId={currentGradeId}
          isActive={activePath === '/workspace-learning-content'}
        />
      )}
      {renderPage(
        '/workspace-evaluation-plan',
        <WorkspaceEvaluationPlanPage isActive={activePath === '/workspace-evaluation-plan'} />
      )}
      {renderPage(
        '/annual-timetable',
        <AnnualTimetablePage
          workspaceId={workspaceId}
          grades={grades}
          currentGradeId={currentGradeId}
          isActive={activePath === '/annual-timetable'}
        />
      )}
      {renderPage(
        '/class-record-memos',
        <ClassRecordMemosPage workspaceId={workspaceId} grades={grades} currentGradeId={currentGradeId} />
      )}
      {renderPage(
        '/weekly-learning-guide',
        <WeeklyLearningGuidePage workspaceId={workspaceId} grades={grades} currentGradeId={currentGradeId} />
      )}
      {renderPage(
        '/todo-note',
        <TodoNotePage
          noteWorkspaceId={workspaceId}
          calendarWorkspaceId={workspaceId}
          variant="workspace"
        />
      )}
      {renderPage(
        '/todo-note/account',
        <TodoNotePage
          noteWorkspaceId={null}
          calendarWorkspaceId={workspaceId}
          variant="account"
        />
      )}
    </>
  );
}

function App() {
  const [school, setSchool] = useState<Workspace | null>(null);
  const [userDisplay, setUserDisplay] = useState<UserDisplayInfo | null>(null);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [currentGradeId, setCurrentGradeId] = useState<string | null>(null);
  const [initialSidebarFocusGradeId, setInitialSidebarFocusGradeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = window.localStorage.getItem('curriculum-theme');
    return stored === 'light' || stored === 'dark' ? stored : 'light';
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [classSettingsModalOpen, setClassSettingsModalOpen] = useState(false);
  const [classMembersSettingsModalOpen, setClassMembersSettingsModalOpen] = useState(false);
  const [passwordRecoveryOpen, setPasswordRecoveryOpen] = useState(false);
  /** 탈퇴 대기(14일 유예) 계정 — 있으면 앱 대신 복구 화면 */
  const [pendingDeletion, setPendingDeletion] = useState<
    { userId: string; email: string; info: PendingAccountDeletion } | null
  >(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signUpModalOpen, setSignUpModalOpen] = useState(false);
  /** unchecked: 동의 기록 조회 전 · required: 동의 필수(앱 진입 차단) · ok: 통과 */
  const [legalConsentStatus, setLegalConsentStatus] = useState<'unchecked' | 'required' | 'ok'>(
    'unchecked'
  );
  const [legalConsentBusy, setLegalConsentBusy] = useState(false);
  const [legalConsentError, setLegalConsentError] = useState<string | null>(null);
  const loadSchoolInFlightRef = useRef<Promise<void> | null>(null);
  /** 현재 로그인 처리된 사용자. supabase-js는 탭이 다시 보일 때 같은 세션으로 SIGNED_IN을 재발행한다. */
  const signedInUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    applyAppDocumentTitle();
  }, []);

  const normalizeFetchError = useCallback((message: string | undefined): string => {
    if (!message) return '초기 로드에 실패했습니다.';
    if (/failed to fetch|networkerror|network error/i.test(message) || message.includes('TypeError')) {
      return '네트워크 연결을 확인해 주세요. Supabase 서버에 접속할 수 없습니다.';
    }
    return message;
  }, []);

  const loadSchool = useCallback(async (options?: { postLoginRefresh?: boolean }) => {
    if (loadSchoolInFlightRef.current) {
      return loadSchoolInFlightRef.current;
    }

    const task = (async () => {
      try {
        const resolvedSchool = await ensureSchoolContext();
        if (options?.postLoginRefresh) {
          markPostLoginSchoolDataNetworkRefresh(resolvedSchool.id);
          resetSchoolDataCacheSyncState(resolvedSchool.id);
        }
        await syncSchoolCachesWithQuery(resolvedSchool.id);
        setSchool(resolvedSchool);
        setError(null);
        const [display, gradeRows, uiPrefs] = await Promise.all([
          getCurrentUserDisplay(resolvedSchool.id, resolvedSchool.name),
          getGrades(resolvedSchool.id),
          getUserWorkspaceUiPreferences(resolvedSchool.id),
        ]);
        setUserDisplay(display ?? null);
        setGrades(gradeRows);

        const localSavedId = window.localStorage.getItem(GRADE_KEY);
        /** 유효 학년 ID. 클래스가 있으면 항상 하나를 선택(통합 보기 제거) */
        let sidebarFocus: string | null;
        if (
          uiPrefs.sidebarFocusGradeId &&
          gradeRows.some((g) => g.id === uiPrefs.sidebarFocusGradeId)
        ) {
          sidebarFocus = uiPrefs.sidebarFocusGradeId;
        } else {
          sidebarFocus = resolveGradeIdInList(
            uiPrefs.sidebarFocusGradeId ?? localSavedId,
            gradeRows
          );
        }
        setInitialSidebarFocusGradeId(sidebarFocus);

        let navGradeId = resolveNavGradeIdFromPreferences(uiPrefs, gradeRows, sidebarFocus);
        if (!navGradeId && gradeRows.length > 0) {
          navGradeId =
            resolveGradeIdInList(localSavedId, gradeRows, sidebarFocus) ?? gradeRows[0].id;
        }
        setCurrentGradeId(navGradeId);
        window.localStorage.setItem(GRADE_KEY, navGradeId);

        if (gradeRows.length > 0 && sidebarFocus && uiPrefs.sidebarFocusGradeId == null) {
          void saveUserWorkspaceUiPreferences(resolvedSchool.id, {
            sidebarFocusGradeId: sidebarFocus,
            navIntegratedView: false,
            navGradeId: navGradeId || null,
          }).catch(() => {});
        }
      } catch (e: any) {
        console.error(e);
        setSchool(null);
        setError(normalizeFetchError(e?.message ?? e?.toString?.()));
      } finally {
        setLoading(false);
        loadSchoolInFlightRef.current = null;
      }
    })();

    loadSchoolInFlightRef.current = task;
    return task;
  }, [normalizeFetchError]);

  useEffect(() => {
    // 로그인 전 랜딩·소개·Q&A·이용권 안내: 항상 라이트 (저장된 다크 설정을 덮어쓰지 않음)
    if (isAuthenticated === false) {
      document.documentElement.dataset.theme = 'light';
      return;
    }
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('curriculum-theme', theme);
  }, [theme, isAuthenticated]);

  useEffect(() => {
    return installDevNetworkTransferMonitor();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingDeletion(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const session = await getSession();
        const uid = session?.user?.id ?? '';
        if (!uid) return;
        const info = await getPendingAccountDeletion(uid);
        if (cancelled) return;
        setPendingDeletion(info ? { userId: uid, email: session?.user?.email ?? '', info } : null);
      } catch {
        /* 조회 실패 시 정상 이용 유지 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    let mounted = true;
    const checkAuthAndBootstrap = async () => {
      try {
        // getSession 전에 소비: OAuth 복귀 직후 URL 해시가 지워져도 pending 플래그로 판별
        const oauthReturn = consumeAuthOAuthPending();
        const session = await withTimeout(getSession(), BOOTSTRAP_SESSION_MS, '세션 확인');
        if (!mounted) return;
        if (session && isAuthIdleExpired()) {
          if (oauthReturn) {
            // 예전에 쓰던 기기의 stale lastActivity로 Google 로그인 직후 튕기는 것 방지
            touchAuthActivity();
          } else {
            await signOut();
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
        }
        setIsAuthenticated(!!session);
        if (!session) {
          bindSubscriptionPassUserId(null);
          setLoading(false);
          return;
        }
        bindSubscriptionPassUserId(session.user?.id ?? null);
        signedInUserIdRef.current = session.user?.id ?? null;
        ensureAuthActivityBaseline();
        // 새로고침·기존 세션 복원: Todo·예산노트·학교(연간·프로그램) head 비교 1회
        markTodoNoteLoginHeadSyncPending();
        markBudgetNoteLoginHeadSyncPending();
        markSchoolDataLoginHeadSyncPending();
        if (
          isProgramContentSlidePath() ||
          isStandaloneExportPreviewPath() ||
          isDashboardPresentationPath()
        ) {
          // 슬라이드·메인 프레젠 탭: 워크스페이스 전체 bootstrap(loadSchool) 생략 — egress 절감
          setLoading(false);
          return;
        }
        await withTimeout(loadSchool(), BOOTSTRAP_WORKSPACE_MS, '워크스페이스·학년 로드');
      } catch (e: any) {
        if (!mounted) return;
        console.error(e);
        const msg = e?.message ?? e?.toString?.() ?? '인증 확인에 실패했습니다.';
        setError(/failed to fetch|networkerror|network error/i.test(msg) || msg.includes('TypeError')
          ? '네트워크 연결을 확인해 주세요. Supabase 서버에 접속할 수 없습니다.'
          : msg);
        setSchool(null);
        setLoading(false);
      }
    };
    void checkAuthAndBootstrap();
    const { data: { subscription } } = onAuthStateChange((session, event) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecoveryOpen(true);
      }

      // 초기 세션은 위 checkAuthAndBootstrap에서 이미 처리한다.
      // 토큰 갱신/사용자 메타 갱신마다 워크스페이스 데이터를 다시 읽으면 불필요한 서버 요청이 늘어난다.
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setIsAuthenticated(!!session);
        bindSubscriptionPassUserId(session?.user?.id ?? null);
        return;
      }

      /** 탭 복귀 시 재발행되는 같은 사용자의 SIGNED_IN은 새 로그인이 아니다 — 워크스페이스 재로드·공지 초기화 생략 */
      const sessionUserId = session?.user?.id ?? null;
      if (event === 'SIGNED_IN' && sessionUserId && sessionUserId === signedInUserIdRef.current) {
        setIsAuthenticated(true);
        bindSubscriptionPassUserId(sessionUserId);
        return;
      }
      signedInUserIdRef.current = sessionUserId;

      if (event === 'SIGNED_IN') {
        clearAdminLearningContentSessionDbSync();
        clearAdminEvaluationPlanSessionDbSync();
        // 로그인마다 공지 모달이 다시 뜨도록 확인 기록 초기화
        clearSchoolNoticeAcks(PLATFORM_CONTENT_SCOPE_KEY);
        // 대시보드 Todo·예산노트·학교 데이터: 로그인 직후 1회 head 비교(새로고침은 bootstrap에서 플래그)
        markTodoNoteLoginHeadSyncPending();
        markBudgetNoteLoginHeadSyncPending();
        markSchoolDataLoginHeadSyncPending();
        touchAuthActivity();
      }

      const loggedIn = !!session;
      setIsAuthenticated(loggedIn);
      bindSubscriptionPassUserId(session?.user?.id ?? null);
      if (loggedIn) {
        setError(null);
        if (
          isProgramContentSlidePath() ||
          isStandaloneExportPreviewPath() ||
          isDashboardPresentationPath()
        ) {
          setLoading(false);
          return;
        }
        void (async () => {
          try {
            await withTimeout(loadSchool({ postLoginRefresh: true }), BOOTSTRAP_WORKSPACE_MS, '워크스페이스·학년 로드');
          } catch (err: unknown) {
            if (!mounted) return;
            console.error(err);
            const msg = err instanceof Error ? err.message : String(err);
            setSchool(null);
            setError(normalizeFetchError(msg));
            setLoading(false);
          }
        })();
      } else {
        removeAllNoteQueries();
        clearAdminLearningContentSessionDbSync();
        clearAdminLearningContentMemoryCaches();
        clearAdminEvaluationPlanSessionDbSync();
        clearProgramScopedScopeServerValidatedRegistry();
        clearPostLoginSchoolDataNetworkRefresh();
        bindSubscriptionPassUserId(null);
        setSchool(null);
        setLoading(false);
        setLegalConsentStatus('unchecked');
        setLegalConsentBusy(false);
        setLegalConsentError(null);
      }
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadSchool]);

  const lastActiveUserId = isAuthenticated ? userDisplay?.userId ?? '' : '';
  useEffect(() => {
    if (!lastActiveUserId) return undefined;
    return startUserLastActiveDailyWatch(lastActiveUserId);
  }, [lastActiveUserId]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return startAuthIdleWatch(() => {
      void (async () => {
        await signOut();
        setIsAuthenticated(false);
        setSchool(null);
        setLoading(false);
        setLegalConsentStatus('unchecked');
        setLegalConsentBusy(false);
        setLegalConsentError(null);
      })();
    });
  }, [isAuthenticated]);

  /**
   * 로그인 직후(Google 자동 회원가입 포함) 동의 기록이 전혀 없는 계정만 1회성 게이트로 동의를 받는다.
   * 동의 전에는 MainLayout을 렌더하지 않아 서비스 이용이 불가능하다.
   */
  useEffect(() => {
    if (!isAuthenticated || loading || !school) return;
    if (legalConsentStatus !== 'unchecked') return;
    let cancelled = false;
    void (async () => {
      try {
        const has = await hasCurrentLegalConsent();
        if (cancelled) return;
        setLegalConsentStatus(has ? 'ok' : 'required');
      } catch (e) {
        console.warn('hasLegalConsentRecord:', e);
        // 조회 실패 시에도 동의 없이 진입시키지 않는다
        if (!cancelled) setLegalConsentStatus('required');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading, school, legalConsentStatus]);

  const handleNavGradeChange = useCallback(
    (gradeId: string) => {
      const id = gradeId.trim();
      if (!id) return;
      setCurrentGradeId(id);
      window.localStorage.setItem(GRADE_KEY, id);
      if (!school?.id) return;
      void saveUserWorkspaceUiPreferences(school.id, {
        navIntegratedView: false,
        navGradeId: id,
      }).catch((err) => {
        console.error('상단 클래스 선택 저장 실패:', err);
      });
    },
    [school?.id]
  );

  const handleProgramFocusGradeChange = useCallback((gradeId: string) => {
    const id = gradeId.trim();
    if (!id) return;
    setCurrentGradeId(id);
    window.localStorage.setItem(GRADE_KEY, id);
  }, []);

  const handleSelectIntegratedClassView = useCallback(() => {
    /** 통합 보기 제거: 호출되어도 첫 클래스로 유지(레거시 경로 호환) */
    const fallback = grades[0]?.id ?? '';
    if (!fallback) {
      setCurrentGradeId('');
      window.localStorage.setItem(GRADE_KEY, '');
      return;
    }
    setCurrentGradeId(fallback);
    window.localStorage.setItem(GRADE_KEY, fallback);
  }, [grades]);

  const handleLogout = useCallback(async () => {
    clearPostLoginSchoolDataNetworkRefresh();
    await signOut();
    bindSubscriptionPassUserId(null);
    setIsAuthenticated(false);
    setLegalConsentStatus('unchecked');
    setLegalConsentBusy(false);
    setLegalConsentError(null);
  }, []);
  const handleSwitchToClassIntegrated = useCallback(() => {
    /** no-op: 통합 클래스 보기 제거 */
  }, []);

  /** 클래스 설정 변경: 전체 bootstrap 대신 grades만 갱신 (오픈·저장 체감 속도) */
  const handleClassDataChange = useCallback(
    async (options?: { selectGradeId?: string }) => {
      if (!school?.id) {
        await loadSchool();
        if (options?.selectGradeId) handleNavGradeChange(options.selectGradeId);
        return;
      }
      try {
        const gradeRows = await getGrades(school.id);
        setGrades(gradeRows);
        if (options?.selectGradeId) {
          handleNavGradeChange(options.selectGradeId);
        } else if (gradeRows.length === 0) {
          setCurrentGradeId('');
          window.localStorage.setItem(GRADE_KEY, '');
        } else {
          setCurrentGradeId((prev) => {
            const id = (prev ?? '').trim();
            if (id && gradeRows.some((g) => g.id === id)) return id;
            const fallback = gradeRows[0]?.id ?? '';
            window.localStorage.setItem(GRADE_KEY, fallback);
            return fallback;
          });
        }
      } catch (e) {
        console.error(e);
        await loadSchool();
        if (options?.selectGradeId) {
          handleNavGradeChange(options.selectGradeId);
        }
      }
    },
    [school?.id, loadSchool, handleNavGradeChange]
  );

  if (isPublicPresentationPath()) {
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/p/:token" element={<PublicPresentationPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isSharedTodoNotePublicPath()) {
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/t/:token" element={<SharedTodoNotePublicPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isChallengePublicPath()) {
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route path="/c/:token" element={<ChallengePublicPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isAppWindowPlaceholderPath()) {
    const placeholderStatus = appWindowPlaceholderStatusLabel();
    if (placeholderStatus) document.title = placeholderStatus;
    return (
      <AppErrorBoundary>
        <div
          className={`bootstrap-state bootstrap-splash bootstrap-splash--${theme}`}
          role="status"
          aria-busy="true"
          aria-label={placeholderStatus || '로딩 중'}
        >
          <div className="bootstrap-splash-inner">
            <div className="bootstrap-splash-spinner" aria-hidden />
            {placeholderStatus ? <p className="bootstrap-splash-status">{placeholderStatus}</p> : null}
          </div>
        </div>
      </AppErrorBoundary>
    );
  }

  const commKind = commPathKind();
  if (commKind === 'host') {
    /* 예전 교사 페이지 → 리모컨(소통 프레젠테이션은 리모컨 대상 목록에서) */
    window.location.replace(`${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/remote`);
    return null;
  }
  if (commKind) {
    const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
    return (
      <AppErrorBoundary>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/comm/:roomId" element={<CommStudentPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isRemoteControllerPath()) {
    const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
    return (
      <AppErrorBoundary>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/remote" element={<RemoteControllerPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isDashboardPresentationPath()) {
    const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
    return (
      <AppErrorBoundary>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/dashboard-presentation" element={<DashboardPresentationPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isProgramContentSlidePath()) {
    if (loading) {
      return (
        <AppErrorBoundary>
          <div
            className={`bootstrap-state bootstrap-splash bootstrap-splash--${theme}`}
            role="status"
            aria-busy="true"
            aria-label="로딩 중"
          >
            <div className="bootstrap-splash-inner">
              <div className="bootstrap-splash-spinner" aria-hidden />
            </div>
          </div>
        </AppErrorBoundary>
      );
    }
    if (isAuthenticated === false) {
      return (
        <AppErrorBoundary>
          <LoginPage
            onLogin={async () => {
              setIsAuthenticated(true);
              setLoading(false);
            }}
            theme={theme}
            onToggleTheme={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
          />
        </AppErrorBoundary>
      );
    }
    const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
    return (
      <AppErrorBoundary>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/program-content-slide" element={<ProgramContentSlidePage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (isStandaloneExportPreviewPath()) {
    const routerBasename = (process.env.PUBLIC_URL || '').replace(/\/$/, '') || undefined;
    return (
      <AppErrorBoundary>
        <BrowserRouter basename={routerBasename}>
          <Routes>
            <Route path="/weekly-learning-guide-a4-preview" element={<WeeklyLearningGuideA4PreviewPage />} />
            <Route path="/document-export-preview" element={<DocumentExportPreviewPage />} />
          </Routes>
        </BrowserRouter>
      </AppErrorBoundary>
    );
  }

  if (pendingDeletion && isAuthenticated) {
    return (
      <AppErrorBoundary>
        <PendingAccountDeletionScreen
          pending={pendingDeletion.info}
          accountEmail={pendingDeletion.email}
          onRestored={() => setPendingDeletion(null)}
          onSignedOut={() => {
            setPendingDeletion(null);
            setIsAuthenticated(false);
            setLoading(false);
          }}
        />
      </AppErrorBoundary>
    );
  }

  if (passwordRecoveryOpen) {
    return (
      <AppErrorBoundary>
        <RecoveryPasswordFormModal
          onCompleted={() => {
            setPasswordRecoveryOpen(false);
            setLoading(true);
            setIsAuthenticated(true);
            setError(null);
            void (async () => {
              try {
                await withTimeout(loadSchool(), BOOTSTRAP_WORKSPACE_MS, '워크스페이스·학년 로드');
              } catch (err: unknown) {
                console.error(err);
                const msg = err instanceof Error ? err.message : String(err);
                setSchool(null);
                setError(normalizeFetchError(msg));
                setLoading(false);
              }
            })();
          }}
        />
      </AppErrorBoundary>
    );
  }

  if (loading && isAuthenticated !== false) {
    return (
      <AppErrorBoundary>
        <div
          className={`bootstrap-state bootstrap-splash bootstrap-splash--${theme}`}
          role="status"
          aria-busy="true"
          aria-label="로딩 중"
        >
          <div className="bootstrap-splash-inner">
            <div className="bootstrap-splash-heading">
              <p className="bootstrap-splash-title">
                <BrandLogo className="cp-brand-logo--splash" />
              </p>
              <p className="bootstrap-splash-version" aria-label={`앱 버전 ${APP_VERSION}`}>
                v{APP_VERSION}
              </p>
            </div>
            <div className="bootstrap-splash-spinner" aria-hidden />
            <CachedAppImage
              file="character_3.webp"
              className="bootstrap-splash-logo"
              alt=""
            />
          </div>
        </div>
      </AppErrorBoundary>
    );
  }

  if (isAuthenticated === false) {
    const openLogin = () => {
      setSignUpModalOpen(false);
      setLoginModalOpen(true);
    };
    const openSignUp = () => {
      setLoginModalOpen(false);
      setSignUpModalOpen(true);
    };
    return (
      <AppErrorBoundary>
        <BrowserRouter>
          <Routes>
            <Route
              path="/intro"
              element={<LandingIntroPage onLoginClick={openLogin} />}
            />
            <Route
              path="/notices"
              element={<LandingNoticesPage onLoginClick={openLogin} />}
            />
            <Route
              path="/qa"
              element={<LandingQaPage onLoginClick={openLogin} />}
            />
            <Route
              path="/pass"
              element={<LandingPassPurchasePage onLoginClick={openLogin} />}
            />
            <Route
              path="*"
              element={
                <LandingPage onLoginClick={openLogin} onSignUpClick={openSignUp} />
              }
            />
          </Routes>
        </BrowserRouter>
        <LoginModal
          open={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
          onLogin={async () => {
            if (window.location.pathname === '/pass' || peekPendingPassCheckout()) {
              markPostLoginPassRedirect();
            }
            setIsAuthenticated(true);
            setLoading(true);
            setError(null);
            try {
              await withTimeout(loadSchool(), BOOTSTRAP_WORKSPACE_MS, '워크스페이스·학년 로드');
            } catch (e: unknown) {
              console.error(e);
              const msg = e instanceof Error ? e.message : String(e);
              setSchool(null);
              setError(normalizeFetchError(msg));
              setLoading(false);
            }
          }}
        />
        <SignUpModal
          open={signUpModalOpen}
          onClose={() => setSignUpModalOpen(false)}
          onSignedIn={async () => {
            if (window.location.pathname === '/pass' || peekPendingPassCheckout()) {
              markPostLoginPassRedirect();
            }
            setSignUpModalOpen(false);
            setIsAuthenticated(true);
            setLoading(true);
            setError(null);
            try {
              await withTimeout(loadSchool(), BOOTSTRAP_WORKSPACE_MS, '워크스페이스·학년 로드');
            } catch (e: unknown) {
              console.error(e);
              const msg = e instanceof Error ? e.message : String(e);
              setSchool(null);
              setError(normalizeFetchError(msg));
              setLoading(false);
            }
          }}
        />
      </AppErrorBoundary>
    );
  }

  if (error || !school) {
    return (
      <div className="bootstrap-state" role="alert">
        <h1 className="bootstrap-splash-title">
          <BrandLogo className="cp-brand-logo--splash" />
        </h1>
        <p className="bootstrap-state-version" aria-label={`앱 버전 ${APP_VERSION}`}>
          v{APP_VERSION}
        </p>
        <p className="error-line">{error ?? '워크스페이스 정보를 불러오지 못했습니다.'}</p>
        <p className="bootstrap-state-hint">
          인터넷 연결과 .env의 REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_ANON_KEY를 확인한 뒤 새로고침해 주세요.
        </p>
      </div>
    );
  }

  // 동의 기록 조회 중 — 본 앱을 잠깐이라도 열지 않음
  if (legalConsentStatus === 'unchecked') {
    return (
      <AppErrorBoundary>
        <div
          className={`bootstrap-state bootstrap-splash bootstrap-splash--${theme}`}
          role="status"
          aria-busy="true"
          aria-label="약관 확인 중"
        >
          <div className="bootstrap-splash-inner">
            <div className="bootstrap-splash-spinner" aria-hidden />
          </div>
        </div>
      </AppErrorBoundary>
    );
  }

  // 미동의 — 동의/로그아웃만 가능, MainLayout·라우트는 렌더하지 않음
  if (legalConsentStatus === 'required') {
    return (
      <AppErrorBoundary>
        <LegalConsentModal
          open
          heading="약관 동의가 필요합니다"
          description={
            legalConsentError
              ? legalConsentError
              : '서비스 이용약관·개인정보처리방침에 동의한 뒤에만 이용할 수 있습니다. 동의하지 않으면 로그아웃됩니다.'
          }
          confirmLabel="동의하고 계속하기"
          cancelLabel="동의하지 않고 로그아웃"
          busy={legalConsentBusy}
          onAgree={async (versions) => {
            setLegalConsentBusy(true);
            setLegalConsentError(null);
            try {
              await recordLegalConsent({
                method: 'existing_user_gate',
                termsVersion: versions.termsVersion,
                privacyVersion: versions.privacyVersion,
              });
              const saved = await hasCurrentLegalConsent();
              if (!saved) {
                setLegalConsentError(
                  '동의 기록이 확인되지 않았습니다. 네트워크를 확인한 뒤 다시 동의해 주세요.'
                );
                return;
              }
              setLegalConsentStatus('ok');
            } catch (e) {
              console.warn('recordLegalConsent(existing_user_gate):', e);
              setLegalConsentError(
                e instanceof Error
                  ? `동의 저장에 실패했습니다: ${e.message}`
                  : '동의 저장에 실패했습니다. 다시 시도해 주세요.'
              );
            } finally {
              setLegalConsentBusy(false);
            }
          }}
          onCancel={() => {
            void handleLogout();
          }}
        />
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <SchoolQueryInvalidationHost workspaceId={school.id} />
      <GradeTrackLeaveProvider>
      <ProgramSidebarProvider
        grades={grades}
        workspaceId={school.id}
        initialSidebarFocusGradeId={initialSidebarFocusGradeId}
        navGradeId={currentGradeId}
        onProgramFocusGradeChange={handleProgramFocusGradeChange}
        onSelectIntegratedClassView={handleSelectIntegratedClassView}
      >
      <LoginSchoolNoticeGate />
      <PostLoginRedirectHost />
      <Routes>
        <Route path="/program-content-slide" element={<ProgramContentSlidePage />} />
        <Route path="/weekly-learning-guide-a4-preview" element={<WeeklyLearningGuideA4PreviewPage />} />
        <Route path="/document-export-preview" element={<DocumentExportPreviewPage />} />
        <Route
          path="*"
          element={
            <MainLayout
              grades={grades}
              currentGradeId={currentGradeId}
              onNavGradeChange={handleNavGradeChange}
              userDisplay={userDisplay}
              theme={theme}
              onToggleTheme={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
              onUserClick={() => setProfileModalOpen(true)}
              profileModalOpen={profileModalOpen}
              onProfileModalClose={() => setProfileModalOpen(false)}
              classSettingsModalOpen={classSettingsModalOpen}
              onClassSettingsModalClose={() => setClassSettingsModalOpen(false)}
              onOpenClassSettings={() => setClassSettingsModalOpen(true)}
              classMembersSettingsModalOpen={classMembersSettingsModalOpen}
              onClassMembersSettingsModalClose={() => setClassMembersSettingsModalOpen(false)}
              onOpenClassMembersSettings={() => setClassMembersSettingsModalOpen(true)}
              workspaceId={school?.id ?? ''}
              onLogout={handleLogout}
              onUserDisplayUpdate={() => void loadSchool()}
              onDataChange={(options) => void handleClassDataChange(options)}
            >
              <AnnualProgressKeepAliveRoutes
                workspaceId={school.id}
                grades={grades}
                currentGradeId={currentGradeId ?? ''}
              />
              <ProgramKeepAliveRoutes />
              <StaticPageKeepAliveRoutes
                workspaceId={school.id}
                grades={grades}
                currentGradeId={currentGradeId ?? ''}
                onSwitchToClassIntegrated={handleSwitchToClassIntegrated}
                onNavGradeChange={handleNavGradeChange}
              />
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={null} />
                <Route
                  path="/class-members"
                  element={
                    <ClassMembersDeepLink onOpen={() => setClassMembersSettingsModalOpen(true)} />
                  }
                />
                <Route path="/grade-track" element={null} />
                <Route path="/academic-calendar" element={null} />
                <Route path="/class-schedule" element={null} />
                <Route path="/workspace-learning-content" element={null} />
                <Route path="/workspace-evaluation-plan" element={null} />
                <Route path="/annual-timetable" element={null} />
                <Route path="/class-record-memos" element={null} />
                <Route path="/weekly-learning-guide" element={null} />
                <Route path="/todo-note" element={null} />
                <Route path="/todo-note/account" element={null} />
                <Route path="/annual-progress-term1" element={null} />
                <Route path="/annual-progress-term2" element={null} />
                <Route path="/program/fixed" element={null} />
                <Route path="/program/project/:projectId" element={null} />
                <Route path="/program/project" element={null} />
                <Route path="/program/track/:trackName" element={null} />
                <Route path="/admin/landing-page" element={<AdminLandingPageCMS />} />
                <Route path="/admin/legal" element={<AdminLegalDocumentsPage />} />
                <Route path="/admin/notices" element={<AdminNoticesPage />} />
                <Route path="/admin/messages" element={<AdminMessagesPage workspaceId={school.id} />} />
                <Route path="/admin/profile-bubbles" element={<AdminProfileBubblesPage />} />
                <Route path="/admin/profile-cards" element={<AdminProfileCardSetsPage />} />
                <Route path="/admin/users" element={<AdminUsersAndPassPage workspaceId={school.id} />} />
                <Route path="/admin/plan-permissions" element={<AdminPlanPermissionsPage />} />
                <Route path="/admin/payment-options" element={<AdminPaymentOptionsPage />} />
                <Route path="/admin/beta-tester" element={<AdminBetaTesterPage />} />
                <Route path="/admin/learning-content" element={<AdminLearningContentPage />} />
                <Route path="/admin/evaluation-plan" element={<AdminEvaluationPlanPage />} />
                <Route path="/admin/cross-curricular" element={<AdminCrossCurricularPage />} />
                <Route path="/admin/learning-plan" element={<AdminLearningPlanActivityPage />} />
                <Route path="/admin/todo-note" element={<Navigate to="/todo-note" replace />} />
                <Route path="/beta-tester" element={<BetaTesterFeedbackPage />} />
                <Route path="/daily-note" element={<DailyNotePage />} />
                <Route path="/budget-note" element={<BudgetNotePage noteWorkspaceId={school.id} variant="workspace" />} />
                <Route
                  path="/budget-note/account"
                  element={<BudgetNotePage noteWorkspaceId={null} variant="account" />}
                />
                <Route path="/admin/budget-note" element={<Navigate to="/budget-note" replace />} />
                <Route
                  path="/assignment-note"
                  element={
                    <AssignmentPointsNotePage
                      workspaceId={school.id}
                      currentGradeId={currentGradeId ?? ''}
                    />
                  }
                />
                <Route
                  path="/assignment-points-note"
                  element={<Navigate to="/assignment-note" replace />}
                />
                <Route
                  path="/point-item-note"
                  element={
                    <PointItemNotePage
                      workspaceId={school.id}
                      currentGradeId={currentGradeId ?? ''}
                      grades={grades}
                    />
                  }
                />
                <Route
                  path="/subscription"
                  element={<SubscriptionPassPage workspaceId={school.id} />}
                />
                <Route path="/pass" element={<Navigate to="/subscription" replace />} />
              </Routes>
            </MainLayout>
          }
        />
      </Routes>
      </ProgramSidebarProvider>
      </GradeTrackLeaveProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
}

/** 공개 법적 고지(/privacy, /terms, /refund): 로그인·워크스페이스 부트스트랩 없이 바로 표시 */
function AppRoot() {
  const legalKind = getLegalDocKindFromPath();
  if (legalKind) return <LegalDocumentPage kind={legalKind} />;
  return <App />;
}

export default AppRoot;
